import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'

export interface ProblemDetails {
  status?: number
  title?: string
  detail?: string
  code?: string
  errors?: Record<string, string[]>
  correlationId?: string
}

export interface AuthTokens {
  accessToken: string
  accessTokenExpiresAt: string
  refreshToken: string
  refreshTokenExpiresAt: string
  user: CurrentUser
}

export interface RoleAssignment {
  role: 'SuperAdmin' | 'TenantAdmin' | 'Auditor' | 'Viewer' | 'Supplier'
  companyId?: string
  siteId?: string
  formTemplateId?: string
}

export interface CurrentUser {
  id: string
  email: string
  fullName: string
  tenantId?: string
  tenantName?: string
  companyName?: string | null
  locale: string
  mustChangePassword: boolean
  roles: RoleAssignment[]
}

const STORAGE_KEY = 'isg.auth'

export const tokenStore = {
  get(): AuthTokens | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? (JSON.parse(raw) as AuthTokens) : null
    } catch {
      return null
    }
  },
  set(tokens: AuthTokens | null) {
    if (tokens) localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens))
    else localStorage.removeItem(STORAGE_KEY)
  },
}

export const api = axios.create({ baseURL: API_BASE_URL, timeout: 30_000 })

api.interceptors.request.use((config) => {
  const tokens = tokenStore.get()
  if (tokens?.accessToken) config.headers.Authorization = `Bearer ${tokens.accessToken}`
  const tenantId = localStorage.getItem('isg.tenantId')
  if (tenantId) config.headers['X-Tenant-Id'] = tenantId
  return config
})

let refreshing: Promise<AuthTokens | null> | null = null

async function refreshTokens(): Promise<AuthTokens | null> {
  const current = tokenStore.get()
  if (!current?.refreshToken) return null
  try {
    const { data } = await axios.post<AuthTokens>(`${API_BASE_URL}/v1/auth/refresh`, { refreshToken: current.refreshToken })
    tokenStore.set(data)
    return data
  } catch {
    tokenStore.set(null)
    return null
  }
}

// Single-flight refresh on 401, then replay the original request once.
api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError<ProblemDetails>) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined
    if (error.response?.status === 401 && original && !original._retried && !original.url?.includes('/auth/')) {
      original._retried = true
      refreshing ??= refreshTokens().finally(() => (refreshing = null))
      const tokens = await refreshing
      if (tokens) {
        original.headers.Authorization = `Bearer ${tokens.accessToken}`
        return api(original)
      }
      window.dispatchEvent(new CustomEvent('isg:logout'))
    }
    return Promise.reject(error)
  },
)

export function problemOf(error: unknown): ProblemDetails {
  if (axios.isAxiosError<ProblemDetails>(error) && error.response?.data) return error.response.data
  return { title: error instanceof Error ? error.message : 'Bilinmeyen hata' }
}
